import { BaseFeature } from '../../core/base-feature.js';

/**
 * InstalledAppsFeature - Frontend feature for accessing installed apps data
 *
 * Provides:
 * - List of installed Windows applications with display names
 * - Application icons as base64 data URLs
 * - Mapping from exe names to friendly display names
 */
export class InstalledAppsFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        // Cached data
        this.installedApps = [];
        this.nameMap = {}; // exe name -> display name
        this.iconCache = new Map(); // exe name -> icon data URL
        this.isLoading = false;
        this.lastRefresh = 0;

        // Pending requests tracking
        this._pendingRequests = new Map();

        // Callbacks
        this.updateCallbacks = new Set();
    }

    static getMetadata() {
        return {
            id: 'installed-apps',
            name: 'installed-apps',
            version: 'v1.0',
            capabilities: [
                'get_installed_apps',
                'get_app_icons',
                'get_name_map'
            ],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[InstalledApps] Initializing...');
        console.log('[InstalledApps] Initialized');
    }

    async handleMessage(message) {
        const { action, payload } = message;

        switch (action) {
            case 'get_all_response':
                return this.handleInstalledAppsResponse(payload);

            case 'get_name_map_response':
                return this.handleNameMapResponse(payload);

            case 'get_icons_batch_response':
                return this.handleIconsBatchResponse(payload);

            case 'get_daily_usage_response':
                return this.handleDailyUsageResponse(payload);

            case 'get_app_history_response':
                return this.handleAppHistoryResponse(payload);

            case 'initial_state':
                // Handle initial state broadcast from desktop
                if (payload.apps) {
                    this.installedApps = payload.apps;
                }
                if (payload.nameMap) {
                    this.nameMap = payload.nameMap;
                }
                this.notifyUpdateListeners();
                break;

            default:
                console.warn(`[InstalledApps] Unknown action: ${action}`);
        }
    }

    // =====================================
    // Data Fetching
    // =====================================

    /**
     * Get all installed applications
     * @param {boolean} forceRefresh - Force refresh from desktop
     * @returns {Promise<Array>} Array of installed app objects
     */
    async getInstalledApps(forceRefresh = false) {
        // Return cached if available and not forcing refresh
        if (!forceRefresh && this.installedApps.length > 0) {
            return this.installedApps;
        }

        this.isLoading = true;

        return new Promise((resolve) => {
            this.sendMessage('get_all', {});
            this._pendingRequests.set('get_all', resolve);

            // Timeout after 30 seconds
            setTimeout(() => {
                if (this._pendingRequests.has('get_all')) {
                    this._pendingRequests.delete('get_all');
                    resolve(this.installedApps);
                }
            }, 30000);
        });
    }

    handleInstalledAppsResponse(payload) {
        this.isLoading = false;

        if (payload.apps) {
            this.installedApps = payload.apps;
            this.lastRefresh = Date.now();
            console.log(`[InstalledApps] Loaded ${this.installedApps.length} apps`);
        }

        const resolve = this._pendingRequests.get('get_all');
        if (resolve) {
            this._pendingRequests.delete('get_all');
            resolve(this.installedApps);
        }

        this.notifyUpdateListeners();
    }

    /**
     * Get exe name to display name mapping
     * @returns {Promise<Object>} Map of exe name -> display name
     */
    async getNameMap() {
        if (Object.keys(this.nameMap).length > 0) {
            return this.nameMap;
        }

        return new Promise((resolve) => {
            const messageId = this.sendMessage('get_name_map', {});
            this._pendingRequests.set('get_name_map', resolve);

            setTimeout(() => {
                if (this._pendingRequests.has('get_name_map')) {
                    this._pendingRequests.delete('get_name_map');
                    resolve(this.nameMap);
                }
            }, 10000);
        });
    }

    handleNameMapResponse(payload) {
        if (payload.map) {
            this.nameMap = payload.map;
            console.log(`[InstalledApps] Loaded name map with ${Object.keys(this.nameMap).length} entries`);
        }

        const resolve = this._pendingRequests.get('get_name_map');
        if (resolve) {
            this._pendingRequests.delete('get_name_map');
            resolve(this.nameMap);
        }
    }

    /**
     * Get icons for multiple executables
     * @param {Array<string>} exeNames - Array of exe names
     * @param {number} size - Icon size (default 32)
     * @returns {Promise<Object>} Map of exe name -> icon data URL
     */
    async getIconsBatch(exeNames, size = 32) {
        // Filter out already cached icons
        const needed = exeNames.filter(name => !this.iconCache.has(name));

        if (needed.length === 0) {
            // All icons are cached
            const result = {};
            exeNames.forEach(name => {
                if (this.iconCache.has(name)) {
                    result[name] = this.iconCache.get(name);
                }
            });
            return result;
        }

        return new Promise((resolve) => {
            const messageId = this.sendMessage('get_icons_batch', { exeNames: needed, size });
            this._pendingRequests.set('get_icons_batch', { resolve, allExeNames: exeNames });

            setTimeout(() => {
                if (this._pendingRequests.has('get_icons_batch')) {
                    this._pendingRequests.delete('get_icons_batch');
                    // Return whatever we have cached
                    const result = {};
                    exeNames.forEach(name => {
                        if (this.iconCache.has(name)) {
                            result[name] = this.iconCache.get(name);
                        }
                    });
                    resolve(result);
                }
            }, 30000);
        });
    }

    handleIconsBatchResponse(payload) {
        const request = this._pendingRequests.get('get_icons_batch');

        if (payload.icons) {
            // Cache the icons
            Object.entries(payload.icons).forEach(([name, icon]) => {
                this.iconCache.set(name, icon);
            });
            console.log(`[InstalledApps] Cached ${Object.keys(payload.icons).length} icons`);
        }

        if (request) {
            this._pendingRequests.delete('get_icons_batch');

            // Build result including cached icons
            const result = {};
            request.allExeNames.forEach(name => {
                if (this.iconCache.has(name)) {
                    result[name] = this.iconCache.get(name);
                }
            });
            request.resolve(result);
        }
    }

    // =====================================
    // Historical Usage Methods
    // =====================================

    /**
     * Get daily usage summaries for a date range
     * @param {string} startDate - Start date in YYYY-MM-DD format
     * @param {string} endDate - End date in YYYY-MM-DD format
     * @returns {Promise<Array>} Array of daily usage summaries
     */
    async getDailyUsage(startDate, endDate) {
        return new Promise((resolve) => {
            this.sendMessage('get_daily_usage', { startDate, endDate });
            this._pendingRequests.set('get_daily_usage', resolve);

            // Timeout after 10 seconds
            setTimeout(() => {
                if (this._pendingRequests.has('get_daily_usage')) {
                    this._pendingRequests.delete('get_daily_usage');
                    resolve([]);
                }
            }, 10000);
        });
    }

    handleDailyUsageResponse(payload) {
        const resolve = this._pendingRequests.get('get_daily_usage');

        if (resolve) {
            this._pendingRequests.delete('get_daily_usage');
            resolve(payload.usage || []);
        }
    }

    /**
     * Get usage history for a specific app
     * @param {string} exeName - Executable name
     * @param {number} days - Number of days to look back (default 30)
     * @returns {Promise<Array>} Array of daily usage for the app
     */
    async getAppHistory(exeName, days = 30) {
        return new Promise((resolve) => {
            this.sendMessage('get_app_history', { exeName, days });
            this._pendingRequests.set('get_app_history', resolve);

            // Timeout after 10 seconds
            setTimeout(() => {
                if (this._pendingRequests.has('get_app_history')) {
                    this._pendingRequests.delete('get_app_history');
                    resolve([]);
                }
            }, 10000);
        });
    }

    handleAppHistoryResponse(payload) {
        const resolve = this._pendingRequests.get('get_app_history');

        if (resolve) {
            this._pendingRequests.delete('get_app_history');
            resolve(payload.history || []);
        }
    }

    // =====================================
    // Convenience Methods
    // =====================================

    /**
     * Get display name for an executable
     * @param {string} exeName - The executable name (e.g., "chrome.exe")
     * @returns {string} Display name or formatted exe name
     */
    getDisplayName(exeName) {
        if (!exeName) return '';

        // Check name map first
        const mapped = this.nameMap[exeName.toLowerCase()];
        if (mapped) return mapped;

        // Check installed apps
        const app = this.installedApps.find(a => a.exeName === exeName.toLowerCase());
        if (app && app.displayName) return app.displayName;

        // Fallback: format exe name
        return exeName.replace(/\.exe$/i, '');
    }

    /**
     * Get icon for an executable (from cache)
     * @param {string} exeName - The executable name
     * @returns {string|null} Icon data URL or null
     */
    getIcon(exeName) {
        if (!exeName) return null;
        return this.iconCache.get(exeName.toLowerCase()) || null;
    }

    /**
     * Find app info by exe name
     * @param {string} exeName - The executable name
     * @returns {Object|null} App info or null
     */
    findApp(exeName) {
        if (!exeName) return null;
        return this.installedApps.find(a => a.exeName === exeName.toLowerCase()) || null;
    }

    // =====================================
    // Event Listeners
    // =====================================

    /**
     * Subscribe to updates
     * @param {Function} callback - Called when data changes
     * @returns {Function} Unsubscribe function
     */
    onUpdate(callback) {
        this.updateCallbacks.add(callback);
        return () => this.updateCallbacks.delete(callback);
    }

    notifyUpdateListeners() {
        const data = {
            installedApps: this.installedApps,
            nameMap: this.nameMap,
            isLoading: this.isLoading
        };

        for (const callback of this.updateCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[InstalledApps] Error in update callback:', error);
            }
        }
    }

    // =====================================
    // Getters
    // =====================================

    getInstalledAppsList() {
        return this.installedApps;
    }

    getNameMapData() {
        return this.nameMap;
    }

    getIsLoading() {
        return this.isLoading;
    }

    // =====================================
    // Cleanup
    // =====================================

    async cleanup() {
        console.log('[InstalledApps] Cleaning up...');
        this.installedApps = [];
        this.nameMap = {};
        this.iconCache.clear();
        this.updateCallbacks.clear();
        this._pendingRequests?.clear();
    }
}
